import { useState, useCallback } from 'react';
import { createApiHeaders, getApiBaseUrl } from '../../../../config/api';
import { DEFAULT_FORM_DATA } from '../../../../config/constants';
import { debugLog } from '../../../../utils/debugLog';

/**
 * useTemplateLoading Hook
 * Handles template loading logic
 */
export const useTemplateLoading = versionId => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedTemplateProvider, setLoadedTemplateProvider] = useState(null);
  const [loadedTemplateModel, setLoadedTemplateModel] = useState(null);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [currentVersionStatus, setCurrentVersionStatus] = useState(null);

  const loadTemplate = useCallback(
    async (id, setFormData, setOriginalTemplateData) => {
      try {
        setLoading(true);
        setError(null);

        const headers = createApiHeaders();
        let response;

        if (versionId) {
          // First, fetch all pages to get all versions and find the version by ID
          const allVersions = [];
          let page = 1;
          let hasMore = true;
          const pageSize = 100; // Use reasonable page size

          while (hasMore) {
            const params = new URLSearchParams({
              page: page.toString(),
              page_size: pageSize.toString(),
              allow_draft: 'true',
            });

            let versionsResponse = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/advanced/${id}/versions?${params.toString()}`,
              { headers }
            );

            if (!versionsResponse.ok && versionsResponse.status === 404) {
              const fallbackParams = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
              });
              versionsResponse = await fetch(
                `${getApiBaseUrl()}/api/v1/templates/${id}/versions?${fallbackParams.toString()}`,
                { headers }
              );
            }

            if (!versionsResponse.ok) {
              throw new Error(
                `Failed to load versions: ${versionsResponse.status}`
              );
            }

            const versionsData = await versionsResponse.json();
            const versionsList =
              versionsData.versions || versionsData.templates || [];
            allVersions.push(...versionsList);

            // Check if there are more pages
            const total = versionsData.total || 0;
            const currentPage = versionsData.page || page;
            const totalPages = Math.ceil(total / pageSize);
            hasMore =
              currentPage < totalPages && versionsList.length === pageSize;

            page++;
          }

          const version = allVersions.find(v => v.id === versionId);

          if (!version) {
            throw new Error(`Version not found: ${versionId}`);
          }

          // Get version_number from the version object
          const versionNumber = version.version_number || version.version;

          // Use advanced endpoint with version_number to get full template data with llm_config
          const params = new URLSearchParams({
            status: 'all',
            allow_draft: 'true',
          });
          if (versionNumber) {
            params.append('version_number', versionNumber.toString());
          }

          let versionResponse = await fetch(
            `${getApiBaseUrl()}/api/v1/templates/advanced/${id}?${params.toString()}`,
            { headers }
          );

          // Fallback to basic endpoint if advanced doesn't exist
          if (!versionResponse.ok && versionResponse.status === 404) {
            const fallbackParams = new URLSearchParams({
              status: 'all',
            });
            if (versionNumber) {
              fallbackParams.append('version', versionNumber.toString());
            }
            versionResponse = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/${id}?${fallbackParams.toString()}`,
              { headers }
            );
          }

          if (!versionResponse.ok) {
            throw new Error(
              `Failed to load version: ${versionResponse.status}`
            );
          }

          setCurrentVersionId(version.id);
          setCurrentVersionStatus(version.status?.toLowerCase() || 'draft');

          response = versionResponse;
        } else {
          const templateUrl = `${getApiBaseUrl()}/api/v1/templates/${id}?status=all`;
          debugLog(
            'Loading template from basic endpoint with status=all:',
            templateUrl
          );

          response = await fetch(templateUrl, { headers });
          debugLog('Template endpoint response status:', response.status);
        }

        if (!response.ok) {
          let errorMessage = `Failed to load template: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch {
            // If response is not JSON, use default message
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        debugLog('Template data loaded:', {
          has_llm_config: !!data.llm_config,
          has_model_config: !!data.model_config,
          llm_config: data.llm_config,
          model_config: data.model_config,
        });

        let normalizedModelConfig = data.llm_config || null;
        if (!normalizedModelConfig && data.model_config) {
          try {
            const raw =
              typeof data.model_config === 'string'
                ? JSON.parse(data.model_config)
                : data.model_config;
            normalizedModelConfig = raw || null;
          } catch (e) {
            debugLog('Failed to parse model_config:', e);
            normalizedModelConfig = null;
          }
        }

        debugLog('Normalized model config:', normalizedModelConfig);

        // Only set provider/model if they exist in the config
        // If missing, leave them null so useTemplateSync can set defaults from available providers
        const templateProvider = normalizedModelConfig?.provider || null;
        const templateModel = normalizedModelConfig?.model_name || null;

        debugLog('Extracted provider/model:', {
          templateProvider,
          templateModel,
        });

        setLoadedTemplateProvider(templateProvider);
        setLoadedTemplateModel(templateModel);

        const templateContent = data.template || data.template_content || '';

        const transformedData = {
          name: data.name || '',
          description: data.description || '',
          template: templateContent,
          category: data.category || 'general',
          tags: data.tags || [],
          output_format: data.output_format || 'text',
          status: data.status || 'draft',
          model_config: {
            // Set provider/model from template if available, otherwise empty
            // useTemplateSync will update these after providers are loaded
            // This ensures the values are visible immediately if available
            provider: templateProvider || '', // Set from template if available
            model_name: templateModel || '', // Set from template if available
            temperature: normalizedModelConfig?.temperature ?? 0.7,
            max_tokens:
              normalizedModelConfig?.max_tokens ??
              DEFAULT_FORM_DATA.model_config.max_tokens,
            top_p: normalizedModelConfig?.top_p ?? 1.0,
            frequency_penalty: normalizedModelConfig?.frequency_penalty ?? 0.0,
            presence_penalty: normalizedModelConfig?.presence_penalty ?? 0.0,
          },
        };

        debugLog('Setting formData with model_config:', {
          provider: transformedData.model_config.provider,
          model_name: transformedData.model_config.model_name,
        });
        setFormData(transformedData);
        setOriginalTemplateData(transformedData);

        if (!versionId) {
          setCurrentVersionId(null);
          setCurrentVersionStatus(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [versionId]
  );

  return {
    loading,
    error,
    setError,
    loadedTemplateProvider,
    loadedTemplateModel,
    currentVersionId,
    currentVersionStatus,
    loadTemplate,
  };
};
